import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { processingFeeApi, F112, F112WithRefs } from '../../../services/processingFeeApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useProcessingFeeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F112[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  const [refData, setRefData] = useState<F112WithRefs | null>(null);

  // Form fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [remark, setRemark] = useState('');
  const [processCode, setProcessCode] = useState('');
  const [isOldCode, setIsOldCode] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load F11U reference data for a given record
  const loadRefData = useCallback(async (f112Code: string) => {
    try {
      const ref = await processingFeeApi.get(f112Code);
      setRefData(ref);
    } catch {
      setRefData(null);
    }
  }, []);

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await processingFeeApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record and load ref data
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f112_01);
      setName(current.f112_11);
      setRemark(current.f112_12);
      setProcessCode(current.f112_14);
      setIsOldCode(current.f112_15);
      setIsDisabled(current.f112_16);
      loadRefData(current.f112_01);
    }
  }, [current, mode, loadRefData]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setName('');
    setRemark('');
    setProcessCode('');
    setIsOldCode(false);
    setIsDisabled(false);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f112_01);
    setName(current.f112_11);
    setRemark(current.f112_12);
    setProcessCode(current.f112_14);
    setIsOldCode(current.f112_15);
    setIsDisabled(current.f112_16);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f112_01);
      setName(current.f112_11);
      setRemark(current.f112_12);
      setProcessCode(current.f112_14);
      setIsOldCode(current.f112_15);
      setIsDisabled(current.f112_16);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await processingFeeApi.create({
          f112_01: code,
          f112_11: name,
          f112_12: remark,
          f112_14: processCode,
          f112_15: isOldCode,
          f112_16: isDisabled,
        });
        message.success('新增成功');
      } else {
        await processingFeeApi.update(current!.f112_01, {
          f112_11: name,
          f112_12: remark,
          f112_14: processCode,
          f112_15: isOldCode,
          f112_16: isDisabled,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f112_01;
      const idx = data.findIndex(r => r.f112_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await processingFeeApi.delete(current.f112_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f112_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx, refData,
    code, setCode,
    name, setName,
    remark, setRemark,
    processCode, setProcessCode,
    isOldCode, setIsOldCode,
    isDisabled, setIsDisabled,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
